import * as React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { motion, AnimatePresence } from 'framer-motion';

import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
import MobileFriendlyIcon from '@mui/icons-material/MobileFriendly';
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded';
import { AddBusiness, AutoAwesomeRounded } from '@mui/icons-material';
import SectionHeading from './SectionHeading';
import Reveal from './motion/Reveal';
import {
  CloudIllustration,
  MobileIllustration,
  DesktopIllustration,
  EndToEndIllustration,
  AIIllustration,
} from './illustrations/TechIllustrations';

const items = [
  {
    icon: <CloudDoneRoundedIcon />,
    title: 'Cloud Applications',
    description:
      'Scalable, secure, high-performance cloud apps for enterprises and startups — built on AWS, Azure, and GCP with microservices and serverless architectures.',
    Illustration: CloudIllustration,
  },
  {
    icon: <MobileFriendlyIcon />,
    title: 'Mobile Applications',
    description:
      'iOS (Swift), Android (Kotlin), and cross-platform (Flutter, React Native) apps engineered for performance, scalability, and a seamless user experience.',
    Illustration: MobileIllustration,
  },
  {
    icon: <DevicesRoundedIcon />,
    title: 'Desktop Applications',
    description:
      'Windows (C++, C#) and macOS (Swift, Objective-C) desktop software with advanced security, automation, and enterprise connectivity.',
    Illustration: DesktopIllustration,
  },
  {
    icon: <AutoAwesomeRounded />,
    title: 'AI & Machine Learning',
    description:
      'LLM-powered features, RAG pipelines, and custom ML models — from intelligent automation to conversational interfaces, built and integrated end to end.',
    Illustration: AIIllustration,
  },
  {
    icon: <AddBusiness />,
    title: 'End-to-End Development',
    description:
      'From ideation and prototyping to deployment and maintenance — full-cycle delivery across cloud, mobile, and desktop platforms.',
    Illustration: EndToEndIllustration,
  },
];

export default function Highlights() {
  const [selected, setSelected] = React.useState(0);
  const active = items[selected];

  return (
    <Container id="highlights" sx={{ pt: { xs: 8, sm: 14 }, pb: { xs: 6, sm: 10 } }}>
      <SectionHeading
        eyebrow="Capabilities"
        title="One team, every"
        highlight="platform"
        subtitle="Whatever your product needs to run on, we design and build it end to end — with a single team that owns architecture, delivery, and outcomes."
      />

      <Box
        sx={{
          mt: { xs: 5, sm: 7 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 4 },
          alignItems: 'stretch',
        }}
      >
        {/* Selector list */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'row', md: 'column' },
            gap: 1.5,
            overflowX: { xs: 'auto', md: 'visible' },
            pb: { xs: 1, md: 0 },
            flex: { md: '0 0 42%' },
          }}
        >
          {items.map((item, index) => {
            const isActive = selected === index;
            return (
              <Box
                key={item.title}
                component="button"
                onClick={() => setSelected(index)}
                sx={{
                  position: 'relative',
                  textAlign: 'left',
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: isActive ? 'primary.main' : 'divider',
                  borderRadius: 2.5,
                  p: { xs: 2, md: 2.5 },
                  minWidth: { xs: 230, md: 'auto' },
                  bgcolor: isActive
                    ? (t) =>
                        t.palette.mode === 'dark'
                          ? 'hsla(210,98%,55%,0.08)'
                          : 'hsla(210,98%,55%,0.06)'
                    : 'transparent',
                  transition: 'all 0.25s ease',
                  overflow: 'hidden',
                  '&:hover': { borderColor: 'primary.main', transform: 'translateX(2px)' },
                }}
              >
                {isActive && (
                  <motion.div
                    layoutId="highlight-active"
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 3,
                      background: 'linear-gradient(hsl(210,98%,55%), hsl(265,90%,62%))',
                    }}
                  />
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                  <Box
                    sx={{
                      color: isActive ? 'primary.main' : 'text.secondary',
                      display: 'flex',
                      transition: 'color 0.25s ease',
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: isActive ? 'text.primary' : 'text.secondary' }}>
                    {item.title}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.6,
                    display: { xs: 'none', md: '-webkit-box' },
                    WebkitLineClamp: isActive ? 'unset' : 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {item.description}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Showcase */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Reveal x={30} y={0}>
            <Card
              variant="outlined"
              sx={{
                position: 'relative',
                height: { xs: 320, sm: 420, md: '100%' },
                minHeight: { md: 460 },
                overflow: 'hidden',
                borderRadius: 3,
                display: 'grid',
                placeItems: 'center',
                background: (t) =>
                  t.palette.mode === 'dark'
                    ? 'radial-gradient(circle at 50% 30%, hsla(210,98%,30%,0.25), transparent 70%)'
                    : 'radial-gradient(circle at 50% 30%, hsla(210,98%,80%,0.4), transparent 70%)',
              }}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={selected}
                  initial={{ opacity: 0, scale: 0.94, y: 16 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.98, y: -16 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 24,
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      maxWidth: 440,
                      height: '72%',
                      filter: 'drop-shadow(0 20px 40px hsla(220,60%,20%,0.35))',
                    }}
                  >
                    <active.Illustration />
                  </Box>
                  <Typography sx={{ mt: 2, fontWeight: 700, fontFamily: '"Sora","Inter",sans-serif' }}>
                    {active.title}
                  </Typography>
                </motion.div>
              </AnimatePresence>
            </Card>
          </Reveal>
        </Box>
      </Box>
    </Container>
  );
}
